import { NavigateOptions, useNavigate, useSearch } from '@tanstack/react-router'

import { IFilters, TIntegratedSystems, TType } from '@/types/tasksApprovals'

export interface IUpdateUrlOpts {
    replace?: boolean
}

const useCustomSearch = (from?: string) => {
    const navigate = useNavigate()
    const filters: IFilters = {
        type: 'all',
        services: {
            all: [],
            tasks: [],
            approvals: [],
        },
    }

    const { type, all, tasks, approvals }: { type: TType; all: string; tasks: string; approvals: TIntegratedSystems } = useSearch({
        from: from ?? '',
    })

    if (type) {
        filters.type = type
    }

    if (all) {
        filters.services.all = decodeURIComponent(all).split(',') as TIntegratedSystems[]
    }

    if (tasks) {
        filters.services.tasks = decodeURIComponent(tasks).split(',') as TIntegratedSystems[]
    }

    if (approvals) {
        filters.services.approvals = decodeURIComponent(approvals).split(',') as TIntegratedSystems[]
    }

    const updateURL = (filters: IFilters, opts?: IUpdateUrlOpts) => {
        // TODO - double check this
        const searchParams: NavigateOptions['search'] = {}
        const services = filters.services

        if (filters.type) {
            searchParams.type = filters.type
        }

        if (services.all.length) {
            searchParams.all = services.all.join(',')
        }

        if (services.tasks.length) {
            searchParams.tasks = services.tasks.join(',')
        }

        if (services.approvals.length) {
            searchParams.approvals = services.approvals.join(',')
        }

        void navigate({
            search: searchParams,
            replace: opts?.replace,
        })
    }

    const updateType = (id: TType) => {
        filters.type = id
        updateURL(filters)
    }

    const updateServices = (
        serviceID: TIntegratedSystems | null,
        type: string,
        emptyFilters?: TIntegratedSystems[],
        opts?: IUpdateUrlOpts,
    ) => {
        const { all, approvals, tasks } = filters.services

        const toggleItem = (arr: TIntegratedSystems[], item: TIntegratedSystems) => {
            const index = arr.indexOf(item)
            if (index > -1) {
                return arr.filter((_, idx) => idx !== index)
            } else {
                return [...arr, item]
            }
        }

        const handleEmptyFilters = (arr: TIntegratedSystems[], emptyFilters: TIntegratedSystems[]) => {
            return emptyFilters.reduce((acc, item) => toggleItem(acc, item), arr)
        }

        switch (type) {
            case 'all':
                filters.services.all = emptyFilters ? handleEmptyFilters(all, emptyFilters) : serviceID ? toggleItem(all, serviceID) : all
                break
            case 'approvals':
                filters.services.approvals = emptyFilters
                    ? handleEmptyFilters(approvals, emptyFilters)
                    : serviceID
                      ? toggleItem(approvals, serviceID)
                      : approvals
                break
            case 'tasks':
                filters.services.tasks = emptyFilters
                    ? handleEmptyFilters(tasks, emptyFilters)
                    : serviceID
                      ? toggleItem(tasks, serviceID)
                      : tasks
                break
            default:
                throw new Error('Invalid filter type')
        }

        updateURL(filters, opts)
    }

    return { filters, updateServices, updateType }
}

export default useCustomSearch
